import { Injectable } from '@angular/core';
import { Document, SignedURLUploadRequest, SignedURLUploadResponse, Upload } from '@ceres/domain';
import { HttpClient, HttpEventType, HttpEvent } from '@angular/common/http';
import { BehaviorSubject, EMPTY, Observable, of, throwError } from 'rxjs';
import { catchError, filter, map, mergeMap, toArray } from 'rxjs/operators';

@Injectable({
    providedIn: 'root'
})
export class DocumentUploadService {
    private readonly _fileUpload = new BehaviorSubject<Map<string, Upload>>(new Map<string, Upload>());
    public readonly fileUpload$: Observable<Map<string, Upload>> = this._fileUpload.asObservable();

    public uploadMultiple(
        httpClient: HttpClient,
        documentUrl: string,
        files: File[],
        folderId: number
    ): Observable<Document[]> {
        let failedUploadCount = 0;

        return of(...files).pipe(
            map((file) => {
                const id = crypto.randomUUID();
                this.addFileUploadEntry(id, file.name);
                return { id, file };
            }),
            mergeMap(({ id, file }) => {
                const payload: SignedURLUploadRequest = {
                    name: file.name,
                    contentType: file.type,
                    folderId
                };
                return httpClient.post<SignedURLUploadResponse>(`${documentUrl}/document`, payload).pipe(
                    map((resp) => ({ resp, file, id })),
                    catchError(() => {
                        failedUploadCount += 1;
                        this.removeFileUploadEntry(id);
                        return EMPTY;
                    })
                );
            }),
            mergeMap(({ resp, file, id }) =>
                httpClient
                    .put(resp.url, file, {
                        reportProgress: true,
                        observe: 'events',
                        responseType: 'json'
                    })
                    .pipe(
                        map((event) => this.handleUploadEvent(id, event)),
                        filter((finished) => finished),
                        map(() => ({ resp, file, id })),
                        catchError(() => {
                            failedUploadCount += 1;
                            this.removeFileUploadEntry(id);
                            return EMPTY;
                        })
                    )
            ),
            mergeMap(({ resp, id }) =>
                httpClient.post<Document>(`${documentUrl}/confirmUpload`, { key: resp.key }).pipe(
                    map((document) => ({ id, document })),
                    catchError(() => {
                        failedUploadCount += 1;
                        this.removeFileUploadEntry(id);
                        return EMPTY;
                    })
                )
            ),
            map(({ id, document }) => {
                this.removeFileUploadEntry(id);
                return document;
            }),
            toArray(),
            mergeMap((documents) => {
                if (documents.length === 0 && failedUploadCount > 0) {
                    return throwError(() => new Error('Upload failed for all selected files.'));
                }

                return of(documents);
            })
        );
    }

    private handleUploadEvent(id: string, event: HttpEvent<unknown>): boolean {
        switch (event.type) {
            case HttpEventType.Sent:
                this.updateUpload(id, HttpEventType.Sent);
                return false;
            case HttpEventType.ResponseHeader:
                this.updateUpload(id, HttpEventType.ResponseHeader);
                return false;
            case HttpEventType.UploadProgress:
                this.updateUpload(
                    id,
                    HttpEventType.UploadProgress,
                    event.total ? Math.round((event.loaded / event.total) * 100) : 0
                );
                return false;
            case HttpEventType.DownloadProgress:
                this.updateUpload(id, HttpEventType.DownloadProgress, 100);
                return false;
            case HttpEventType.Response:
                if (event.status === 200) {
                    this.updateUpload(id, HttpEventType.Response, 100);
                    return true;
                } else {
                    this.updateUpload(id, HttpEventType.Response, 0);
                    return false;
                }
            default:
                return false;
        }
    }

    private addFileUploadEntry(id: string, name: string): void {
        const fileUpload = new Map(this._fileUpload.getValue());
        fileUpload.set(id, { id, name });
        this._fileUpload.next(fileUpload);
    }

    private removeFileUploadEntry(id: string): void {
        const fileUpload = new Map(this._fileUpload.getValue());
        fileUpload.delete(id);
        this._fileUpload.next(fileUpload);
    }

    private updateUpload(id: string, status: HttpEventType, progress = 0): void {
        const fileUpload = new Map(this._fileUpload.getValue());
        const upload = fileUpload.get(id);
        if (upload) {
            fileUpload.set(id, { ...upload, status, progress });
            this._fileUpload.next(fileUpload);
        }
    }
}
